<template>
  <div class="pl-login">
    <!-- ===================== BRAND PANEL ===================== -->
    <div class="pl-brand pl-base-bg">
      <div class="pl-glow pl-glow--a"></div>
      <div class="pl-glow pl-glow--b"></div>

      <div class="pl-brand-content">
        <div class="pl-icon-tile">
          <div class="pl-icon-clip">
            <img src="@/assets/ProfitLedgex.png" alt="ProfitLedgex" class="pl-icon-img" />
          </div>
        </div>
        <h1 class="pl-wordmark">ProfitLedgex</h1>
        <p class="pl-tagline">Intelligent business insights with seamless invoicing.</p>
        <div class="pl-accent-bar"></div>
      </div>
    </div>

    <!-- ===================== FORM PANEL ===================== -->
    <div class="pl-form-panel">
      <div class="pl-form" v-if="loadingInvite">
        <p class="pl-sub">Loading your invite&hellip;</p>
      </div>

      <div class="pl-form" v-else-if="loadError">
        <p class="pl-eyebrow">Invite</p>
        <h2 class="pl-heading">This invite link isn't valid</h2>
        <p class="pl-sub">{{ loadError }}</p>
        <router-link to="/login" class="pl-switch-link">Go to login</router-link>
      </div>

      <div class="pl-form" v-else>
        <p class="pl-eyebrow">You're invited</p>
        <h2 class="pl-heading">Join {{ invite.ownerBusinessName || "the team" }}</h2>
        <p class="pl-sub">
          {{ invite.name }}, you've been invited with
          <strong>{{ invite.teamRole === "VIEW_ONLY" ? "view-only" : "full" }} access</strong>.
          Set a password to finish creating your login.
        </p>

        <v-form ref="acceptForm" v-model="isFormValid" lazy-validation @submit.prevent="handleAccept">
          <label class="pl-label">Email</label>
          <v-text-field
            :value="invite.email"
            outlined
            dense
            hide-details="auto"
            disabled
            class="pl-field" />

          <label class="pl-label">Phone number</label>
          <v-text-field
            v-model.trim="phone"
            type="tel"
            placeholder="10-digit mobile number"
            autocomplete="tel"
            outlined
            hide-details="auto"
            :rules="phoneRules"
            class="pl-field"
            color="#0a6cc0" />

          <label class="pl-label">Password</label>
          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="At least 6 characters"
            autocomplete="new-password"
            outlined
            hide-details="auto"
            :rules="passwordRules"
            class="pl-field"
            color="#0a6cc0">
            <template #append>
              <v-icon style="cursor: pointer;" @click="showPassword = !showPassword">
                {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>

          <v-checkbox
            v-model="acceptedTerms"
            :rules="termsRules"
            hide-details="auto"
            class="pl-terms-check mt-4"
            color="#0a6cc0">
            <template #label>
              <span class="pl-terms-label">
                I agree to the
                <router-link to="/terms" target="_blank" @click.native.stop>Terms &amp; Conditions</router-link>
                and
                <router-link to="/privacy" target="_blank" @click.native.stop>Privacy Policy</router-link>
              </span>
            </template>
          </v-checkbox>

          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">
            {{ errorMessage }}
          </v-alert>

          <v-btn
            type="submit"
            block
            depressed
            height="54"
            :loading="submitting"
            :disabled="!isFormValid || !acceptedTerms || submitting"
            class="pl-submit mt-5">
            Accept &amp; Continue
          </v-btn>
        </v-form>
      </div>
    </div>
  </div>
</template>

<script>
import { TERMS_VERSION } from "@/utils/legal";

export default {
  name: "AcceptInvite",

  data() {
    return {
      loadingInvite: true,
      loadError: "",
      invite: null,

      phone: "",
      password: "",
      acceptedTerms: false,
      showPassword: false,
      isFormValid: false,
      submitting: false,
      errorMessage: "",

      phoneRules: [
        (v) => !!v || "Phone number is required",
        (v) => /^\d{10}$/.test(v) || "Enter a valid 10-digit phone number",
      ],
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => v.length >= 6 || "Password must be at least 6 characters",
      ],
      termsRules: [
        (v) => !!v || "You must agree to the Terms & Conditions and Privacy Policy",
      ],
    };
  },

  async created() {
    const token = this.$route.params.token;
    try {
      this.invite = await this.$store.dispatch("team/getInviteDetails", token);
    } catch (err) {
      this.loadError = err?.response?.data?.message || "This invite link is invalid or has expired";
    } finally {
      this.loadingInvite = false;
    }
  },

  methods: {
    async handleAccept() {
      this.errorMessage = "";
      if (!this.$refs.acceptForm.validate()) return;

      this.submitting = true;
      try {
        const token = this.$route.params.token;
        await this.$store.dispatch("team/acceptInvite", {
          token,
          payload: {
            phone: this.phone,
            password: this.password,
            termsAccepted: this.acceptedTerms,
            termsVersion: TERMS_VERSION,
          },
        });
        await this.$store.dispatch("auth/restoreSession");
        this.$router.push("/dashboard");
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || "Failed to accept invite";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.pl-login {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  font-family: 'Manrope', system-ui, sans-serif;
  color: #0e2433;
}

.pl-brand {
  position: relative;
  flex: 0 0 54%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pl-glow { position: absolute; border-radius: 50%; pointer-events: none; }
.pl-glow--a {
  top: -90px; left: -90px;
  width: 440px; height: 440px;
  background: radial-gradient(circle, rgba(190,235,255,0.95) 0%, rgba(110,195,255,0.60) 32%, rgba(60,140,255,0.18) 58%, transparent 72%);
  filter: blur(38px);
}
.pl-glow--b {
  bottom: -160px; right: -80px;
  width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(50,220,130,0.28), transparent 68%);
  filter: blur(90px);
}

.pl-brand-content {
  position: relative; z-index: 3;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 48px; text-align: center;
}

.pl-icon-tile {
  padding: 16px;
  border-radius: 36px;
  background: rgba(255,255,255,0.12);
  box-shadow: 0 28px 64px rgba(2,20,40,0.50), inset 0 0 0 1.5px rgba(255,255,255,0.22);
  backdrop-filter: blur(10px);
}
.pl-icon-clip {
  width: 128px; height: 128px;
  overflow: hidden;
  border-radius: 26px;
  box-shadow: 0 8px 24px rgba(2,20,40,0.40);
  position: relative;
}
.pl-icon-img {
  display: block; position: absolute; top: 0; left: 0;
  width: 128px; height: auto;
}

.pl-wordmark {
  margin: 28px 0 0;
  font-weight: 800;
  font-size: 46px;
  letter-spacing: -0.025em;
  color: #fff;
  text-shadow: 0 2px 12px rgba(2,20,40,0.35);
}
.pl-tagline {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 500;
  color: rgba(225,245,255,0.88);
}
.pl-accent-bar {
  margin-top: 28px;
  height: 3px; width: 56px;
  border-radius: 3px;
  background: linear-gradient(90deg, #7fd4ff, #74f0a8);
}

.pl-form-panel {
  position: relative;
  flex: 1 1 46%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  background: #f4f6f9;
  overflow-y: auto;
}

.pl-form {
  width: 100%;
  max-width: 400px;
  padding: 32px 0;
}

.pl-eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0a8a6a;
}

.pl-heading {
  margin: 10px 0 6px;
  font-weight: 800;
  font-size: 30px;
  letter-spacing: -0.025em;
  color: #0b1e2d;
  line-height: 1.2;
}

.pl-sub {
  margin: 0 0 26px;
  font-size: 14.5px;
  color: #64747f;
}

.pl-label {
  display: block;
  margin: 16px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374955;
}

.pl-field >>> .v-input__slot {
  border-radius: 12px !important;
  background: #ffffff !important;
  min-height: 50px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
}
.pl-field >>> fieldset {
  border-color: #cdd5dc !important;
  border-width: 1.5px !important;
}
.pl-field >>> .v-input--is-focused fieldset {
  border-color: #0a6cc0 !important;
  border-width: 2px !important;
}

.pl-submit {
  border-radius: 12px !important;
  background: linear-gradient(135deg, #0a5fb5 0%, #0d8f75 52%, #16b358 100%) !important;
  color: #fff !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  text-transform: none !important;
  box-shadow: 0 8px 24px rgba(10,108,192,0.30), 0 4px 8px rgba(13,143,117,0.20) !important;
}

.pl-switch-link {
  color: #0a6cc0;
  font-weight: 700;
  text-decoration: none;
}
.pl-switch-link:hover { text-decoration: underline; }

.pl-terms-check >>> .v-label {
  font-size: 13px;
  color: #374955;
}
.pl-terms-label a {
  color: #0a6cc0;
  font-weight: 700;
  text-decoration: none;
}
.pl-terms-label a:hover { text-decoration: underline; }

@media (max-width: 860px) {
  .pl-login {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }
  .pl-brand {
    flex: 0 0 auto;
    height: 220px;
  }
  .pl-icon-clip { width: 76px; height: 76px; border-radius: 16px; }
  .pl-icon-img { width: 76px; height: auto; }
  .pl-wordmark { font-size: 26px; margin: 12px 0 0; }
  .pl-tagline { font-size: 13px; margin: 6px 0 0; }
  .pl-form-panel { flex: 1; padding: 36px 24px 52px; align-items: flex-start; }
  .pl-form { max-width: 100%; padding: 0; }
  .pl-heading { font-size: 24px; }
}
</style>
